/**
 * Magic Wand Tool - Color removal and transparency feature
 *
 * This module provides functionality to remove specific colors from images
 * and make pixels transparent, similar to a "magic wand" or "background remover" tool.
 *
 * @module magicWand
 */

import magicWandManager from './MagicWandManager';
import {
  initMagicWand,
  isMagicWandActive,
  stopMagicWand,
} from './initMagicWand';
import {
  removeColorFromImageData,
  hexToRgb,
  rgbToHex,
  getColorAtPoint,
} from './colorRemoval';

// Export initialization functions
export { initMagicWand, isMagicWandActive, stopMagicWand };

// Export manager for advanced usage
export { magicWandManager };

// Export utility functions for API usage
export { removeColorFromImageData, hexToRgb, rgbToHex, getColorAtPoint };

/**
 * Public API for Magic Wand tool
 * Can be used by other features to programmatically remove colors from images
 */
export const MagicWandAPI = {
  /**
   * Remove a color from a Fabric.js image
   * @param {fabric.Image} image - The image to process
   * @param {Object} options - Processing options
   * @param {string} options.color - Hex color to remove (default: '#ffffff')
   * @param {number} options.tolerance - Color tolerance 0-100 (default: 50)
   * @param {string} options.smoothing - Smoothing type (default: 'gaussian')
   * @param {number} options.radius - Smooth radius 1-10 (default: 5)
   * @returns {Promise<fabric.Image>} - Processed image
   */
  removeColor: async (image, options = {}) => {
    return magicWandManager.removeColorFromImage(image, options);
  },

  /**
   * Remove a color from raw image data
   * @param {ImageData} imageData - The image data to process
   * @param {string} color - Hex color to remove
   * @param {number} tolerance - Color tolerance
   * @returns {ImageData} - Processed image data
   */
  removeColorFromData: (imageData, color, tolerance = 50) => {
    const rgb = hexToRgb(color);
    if (!rgb) throw new Error('Invalid color format');
    return removeColorFromImageData(imageData, rgb, tolerance);
  },

  /**
   * Get color from a point on canvas
   * @param {HTMLCanvasElement} canvas - The canvas element
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {string} - Hex color at point
   */
  getColorAt: (canvas, x, y) => {
    const ctx = canvas.getContext('2d');
    const color = getColorAtPoint(ctx, x, y);
    return rgbToHex(color.r, color.g, color.b);
  },

  /**
   * Check if Magic Wand is currently active
   * @returns {boolean}
   */
  isActive: () => isMagicWandActive(),

  /**
   * Stop Magic Wand if active
   */
  stop: () => stopMagicWand(),
};

// Default export
export default MagicWandAPI;
